/**
 * App shell: hash routes for the main menu (#/), session setup (#/play) and the game (#/game),
 * plus the pause overlay. Settings resolve from the URL, then the last session, then defaults;
 * a URL carrying ?airport= is a deep link and starts the game directly. See docs/game-shell.md.
 */
import '@fontsource/b612/400.css';
import '@fontsource/b612/700.css';
import '@fontsource/b612-mono/400.css';
import '@fontsource/b612-mono/700.css';
import '@atc/radar/style.css';
import '@atc/tower-view/tv.css';
import './style.css';
import { PILOT_PRACTICE } from './academy/pilot-practice';
import { LESSONS } from './academy/lessons';
import { TUTORIAL } from './academy/tutorial';
import { completedCount, isPassed, markAttempt, markCompleted, readProgress, writeProgress, type AssistLevel } from './academy/progress';
import { airportEntry, loadAirport } from './airports';
import { showBriefing } from './briefing/briefing';
import { enterFullscreen, toggleFullscreen } from './fullscreen';
import type { Stats } from '@atc/sim';
import { startGame, type GameSession } from './game';
import { awardedBadgeIds, BADGES, isEarned } from './academy/badges';
import { showAcademy } from './screens/academy';
import { showAdmin } from './screens/admin';
import { showAccount, showLegal } from './screens/account';
import { showDebrief } from './screens/debrief';
import { showDataNotice, showFeedback } from './screens/feedback';
import { showMainMenu } from './screens/main-menu';
import { showPause } from './screens/pause';
import { downloadSessionLog } from './session-log';
import { showSetup } from './screens/setup';
import { showTranscripts } from './screens/transcripts';
import { primeRadioPlayback } from './radio';
import { browserStorage, DEFAULT_GAME_CONFIG, isDeepLink, loadGameConfig, readFromStorage, readFromUrl, resolveSettings, sanitize, saveSettings, STORAGE_KEY, toUrlParams, type ManagedGameConfig, type SessionSettings } from './settings';
import { grade, recordScore, type ShiftRecord } from './shift';
import { firstActivation, trackEvent, trackPageview } from './analytics';
import { anonymousId, captureDiagnostics, NOTICE_KEY, startTelemetry, type TelemetrySession } from './telemetry';
import { clearAttribution, setAttribution } from './attribution';

const app = document.querySelector<HTMLElement>('#app')!;
const overlayRoot = document.querySelector<HTMLElement>('#overlay-root')!;
const errorEl = document.querySelector<HTMLElement>('#error')!;
const storage = browserStorage();
const anonymous = anonymousId(storage);
const diagnostics = captureDiagnostics();
const startParams = new URLSearchParams(location.search);
let managedConfig: ManagedGameConfig = DEFAULT_GAME_CONFIG;
let settings = resolveSettings(readFromUrl(startParams), readFromStorage(storage), managedConfig);

let game: GameSession | null = null;
/** Route and settings of the running session; a lesson overrides airport and realism without saving them. */
let gameRoute = '';
let sessionSettings: SessionSettings | null = null;
let closeScreen: (() => void) | null = null;
let closeOverlay: (() => void) | null = null;
let closeFeedback: (() => void) | null = null;
/** `?idle=<minutes>` (fractions allowed) tries the auto-pause quickly. Read once: routes rewrite the query. */
const IDLE_OVERRIDE = Number(new URLSearchParams(location.search).get('idle'));
let telemetry: TelemetrySession | null = null;
/** Bumped on every navigation so a slow airport load cannot mount a stale game. */
let navId = 0;

function update(patch: Partial<SessionSettings>): void {
  settings = { ...settings, ...patch };
  saveSettings(settings, storage);
  if (game && !gameRoute.startsWith('#/academy/') && gameRoute !== '#/tutorial') setQuery(toUrlParams(settings).toString());
}
function setQuery(query: string): void {
  history.replaceState(null, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
}
function showError(message: string): void { errorEl.textContent = message; errorEl.hidden = false; }
function hideOverlay(): void { closeOverlay?.(); closeOverlay = null; }
function clearScreen(): void { closeScreen?.(); closeScreen = null; }
/** Watches the running game for the funnel's firsts; cleared with the game. */
let milestones: ReturnType<typeof setInterval> | null = null;
function leaveGame(): void { if (milestones) clearInterval(milestones); milestones = null; hideOverlay(); closeFeedback?.(); closeFeedback = null; telemetry?.abandon(); telemetry?.dispose(); telemetry = null; clearAttribution(); game?.destroy(); game = null; gameRoute = ''; sessionSettings = null; document.title = 'Free Air Traffic Control Simulator Game | ATC AI Simulator'; }

function openFeedback(): void {
  if (closeFeedback) return;
  if (!managedConfig.feedbackEnabled) { showError('Feedback is unavailable while the application service is offline or disabled.'); return; }
  const transcript = game?.transcript();
  closeFeedback = showFeedback(overlayRoot, { anonymousId: anonymous, sessionId: transcript?.id, context: { route: location.hash, airport: sessionSettings?.airport ?? settings.airport, mode: sessionSettings?.mode ?? settings.mode, simTimeS: game?.sim.simTimeS ?? 0 }, onClose: () => { closeFeedback?.(); closeFeedback = null; } });
}

async function route(): Promise<void> {
  const id = ++navId;
  const hash = location.hash || '#/';
  trackPageview(hash);
  if (hash === '#/game' || hash === '#/tutorial' || hash.startsWith('#/academy/')) {
    if (game && gameRoute === hash) return;
    leaveGame();
    await enterGame(id);
    return;
  }
  leaveGame();
  clearScreen();
  setQuery('');
  if (hash === '#/admin') {
    closeScreen = showAdmin(app);
  } else if (hash === '#/account') {
    closeScreen = showAccount(app);
  } else if (hash === '#/terms' || hash === '#/privacy' || hash === '#/refunds') {
    closeScreen = showLegal(app, hash.slice(2) as 'terms' | 'privacy' | 'refunds');
  } else if (hash === '#/play') {
    closeScreen = showSetup(app, {
      settings,
      allowVoicePlayback: managedConfig.allowVoicePlayback,
      allowSpeechInput: managedConfig.allowSpeechInput,
      // Full screen needs the Start click's user gesture, so request it before anything async.
      onStart: (s) => { if (s.fullscreen) void enterFullscreen(); if (s.voice && s.radioProvider === 'openai') primeRadioPlayback(); update({ ...s, voice: managedConfig.allowVoicePlayback && s.voice, speechInput: managedConfig.allowSpeechInput && s.speechInput, seed: Date.now() % 2147483647 }); location.hash = '#/game'; },
    });
  } else if (hash === '#/briefing' || hash.startsWith('#/briefing/')) {
    const icao = hash.slice('#/briefing/'.length) || settings.airport;
    const entry = airportEntry(icao);
    if (!entry) { location.hash = `#/briefing/${settings.airport}`; return; }
    app.innerHTML = `<p class="loading">Loading ${entry.name}</p>`;
    try {
      const airport = await loadAirport(entry.icao);
      if (id !== navId) return;
      app.replaceChildren();
      closeScreen = showBriefing(app, airport, { onClose: () => { location.hash = '#/'; }, closeLabel: 'Main menu', airportLinks: true });
    } catch (err) {
      if (id !== navId) return;
      showError(`${err instanceof Error ? err.message : String(err)}.`);
      location.hash = '#/';
    }
  } else if (hash === '#/first-shift') {
    startFirstShift();
  } else if (hash === '#/academy') {
    closeScreen = showAcademy(app, { progress: readProgress(storage) });
  } else if (hash === '#/transcripts') {
    closeScreen = showTranscripts(app);
  } else {
    if (hash !== '#/') history.replaceState(null, '', `${location.pathname}#/`);
    const progress = readProgress(storage);
    // The highest tier rating held, so the menu says what the player is rated as, not just a count.
    const rating = [...BADGES].reverse().find((b) => b.id.startsWith('tier-') && (progress.badges.includes(b.id) || isEarned(b, progress, LESSONS)))?.title;
    // Nothing saved and nothing passed: somebody who has never played. They get the landing.
    const firstVisit = (() => { try { return !storage?.getItem(STORAGE_KEY) && !Object.keys(progress.lessons).length; } catch { return false; } })();
    closeScreen = showMainMenu(app, { airport: settings.airport, academyDone: completedCount(progress, LESSONS), rating, firstVisit, onFeedback: openFeedback,
      commCheck: managedConfig.allowVoicePlayback ? { provider: settings.radioProvider, pace: settings.radioPace, noise: settings.radioNoise, accents: settings.radioAccents } : undefined });
  }
}

async function enterGame(id: number): Promise<void> {
  managedConfig = await loadGameConfig();
  settings = resolveSettings({}, sanitize(settings), managedConfig);
  if (!managedConfig.allowVoicePlayback) settings.voice = false;
  if (!managedConfig.allowSpeechInput) settings.speechInput = false;
  if (settings.seed === undefined) update({ seed: Date.now() % 2147483647 });
  clearScreen();
  const hash = location.hash;
  const tutorial = hash === '#/tutorial';
  const lessonId = hash.startsWith('#/academy/') ? decodeURIComponent(hash.slice('#/academy/'.length)) : null;
  const lesson = tutorial ? TUTORIAL : lessonId ? [...LESSONS, ...PILOT_PRACTICE].find((l) => l.id === lessonId) : undefined;
  if (lessonId && !lesson) { location.hash = '#/academy'; return; }
  // Only the training route has a next lesson; a practice drill is reached from the reference page.
  const routeIdx = lesson ? LESSONS.findIndex((l) => l.id === lesson.id) : -1;
  const next = routeIdx >= 0 ? LESSONS[routeIdx + 1] : undefined;
  // A lesson fixes the airport and the session type, and needs the radar scope visible. Everything
  // else — realism, pilots, language, voice — is the configuration the player is already using.
  const s: SessionSettings = tutorial
    ? { ...settings, airport: 'llbg', position: 'legacy', mode: 'free', layout: 'split', speechInput: managedConfig.allowSpeechInput, realism: 'low', assists: true, side: true, arrivalRunways: ['12'], departureRunways: ['12'], pilotBrain: 'rules', procedures: 'active', timeOfDay: 'day', weather: 'clear' }
    : lesson ? { ...settings, airport: lesson.airport, mode: 'free', layout: settings.layout === 'tower' ? 'radar' : settings.layout } : settings;
  app.innerHTML = `<p class="loading">Loading ${airportEntry(s.airport)?.name ?? s.airport.toUpperCase()}</p>`;
  try {
    const airport = await loadAirport(s.airport);
    if (id !== navId) return;
    errorEl.hidden = true;
    setQuery(lesson ? '' : toUrlParams(s).toString());
    gameRoute = hash;
    sessionSettings = s;
    game = startGame(app, airport, s, {
      onMenu: openPause,
      onAirport: (icao) => restart({ airport: icao }),
      onSettingsChange: (patch) => {
        // A lesson fixes its airport; everything else it changes, realism included, is the player's.
        if (!lesson) { update(patch); return; }
        const { airport: _airport, ...rest } = patch;
        update(rest);
      },
      onError: showError,
      onEnd: showEnd,
      onBriefing: () => openBriefing(),
      onRestart: () => restart(),
      onLessonComplete: (done, assist) => {
        trackEvent('lesson_complete', { lesson: done, assist: String(assist) }, `${done}:${game?.transcript().id}`);
        if (tutorial) return;
        const progress = markCompleted(storage, done, assist);
        writeProgress(storage, { ...progress, badges: awardedBadgeIds(progress, LESSONS) });
      },
      onLessonExit: () => { location.hash = tutorial ? '#/' : '#/academy'; },
      onLessonNext: () => { if (next) location.hash = `#/academy/${next.id}`; },
      onTutorialWatch: () => { location.hash = '#/play'; },
      onFirstShift: () => { location.hash = '#/first-shift'; },
      // One checkpoint as the game holds, then quiet until someone comes back to it.
      onIdlePause: (reason, paused) => { if (paused) { telemetry?.checkpoint(); trackEvent('idle_pause', { reason }); } telemetry?.setQuiet(paused); },
    }, { lesson, nextLessonTitle: next?.title, offerShift: lesson && !tutorial ? finishesBeginnerTier(lesson.id) : false, allowSpeechInput: managedConfig.allowSpeechInput, allowVoicePlayback: managedConfig.allowVoicePlayback, idlePauseMinutes: idleMinutes() });
    if (lesson && !tutorial) markAttempt(storage, lesson.id);
    // From here the service can attribute what the pilots cost to this game and this browser.
    setAttribution({ sessionId: game.transcript().id, anonymousId: anonymous });
    const gameId = game.transcript().id, kind = tutorial ? 'tutorial' : lesson ? 'academy' : s.mode;
    trackEvent(lesson ? 'lesson_start' : 'game_start', { airport: s.airport, mode: kind, realism: s.realism, mic: s.speechInput, ...(lesson ? { lesson: lesson.id } : {}) }, gameId);
    // Accepted clearances and landings are in the sim's stats; a glance every two seconds is plenty.
    milestones = setInterval(() => {
      const st = game?.sim.stats(); if (!st) return;
      if (st.commands - st.rejectedCommands > 0) trackEvent('first_clearance', { mode: kind, firstEver: firstActivation(storage) }, gameId);
      if (st.landings > 0) trackEvent('first_landing', { mode: kind }, gameId);
    }, 2000);
    telemetry = startTelemetry(anonymous, () => ({ settings: s, transcript: game!.transcript(), stats: { ...game!.sim.stats(), idlePauses: game!.idlePauses() }, costs: game!.costs(), errors: diagnostics.values }), managedConfig.telemetryEnabled, lesson ? { lessonId: lesson.id } : {});
  } catch (err) {
    if (id !== navId) return;
    showError(`${err instanceof Error ? err.message : String(err)}. Pick another airport.`);
    location.hash = tutorial ? '#/' : lesson ? '#/academy' : '#/play';
  }
}

/** The server's setting, or the override above. */
function idleMinutes(): number {
  return Number.isFinite(IDLE_OVERRIDE) && IDLE_OVERRIDE > 0 ? IDLE_OVERRIDE : managedConfig.idlePauseMinutes;
}

/** True when passing this lesson completes the beginner tier, or it is the last lesson on the route. */
function finishesBeginnerTier(id: string): boolean {
  const lesson = LESSONS.find((l) => l.id === id);
  if (!lesson) return false;
  if (lesson === LESSONS.at(-1)) return true;
  const progress = readProgress(storage);
  return lesson.tier === 'beginner' && LESSONS.filter((l) => l.tier === 'beginner' && l.id !== id).every((l) => isPassed(progress, l.id));
}

/**
 * The Academy's hand-off to live traffic: fifteen minutes at KSFO on the tower position, light
 * traffic, Mid realism with the assists on. The player's other choices (voice, layout extras)
 * carry over, and the setup screen is skipped because the choice has already been made for them.
 */
function startFirstShift(): void {
  update({ airport: 'ksfo', position: 'tower', layout: 'split', mode: 'shift', shiftMinutes: 15, traffic: 'light', realism: 'mid', assists: true, atis: true, seed: Date.now() % 2147483647 });
  location.hash = '#/game';
}

function restart(patch: Partial<SessionSettings> = {}): void {
  leaveGame();
  update(patch);
  void enterGame(++navId);
}

function openPause(): void {
  if (!game || closeOverlay) return;
  const session = game;
  session.pause();
  closeOverlay = showPause(overlayRoot, {
    settings,
    allowVoicePlayback: managedConfig.allowVoicePlayback,
    allowSpeechInput: managedConfig.allowSpeechInput,
    ai: session.ai(),
    onResume: () => { hideOverlay(); session.resume(); },
    onChange: (patch) => { update(patch); session.apply(patch); },
    onFullscreen: () => void toggleFullscreen(),
    onRestart: () => restart(),
    onEnd: () => session.end(),
    briefingLabel: gameRoute.startsWith('#/academy/') || gameRoute === '#/tutorial' ? 'Lesson briefing' : 'Airport briefing',
    onBriefing: () => { hideOverlay(); openBriefing(session, true); },
    onFeedback: openFeedback,
    onSessionLog: () => downloadSessionLog(session.sessionLog()),
    onQuit: () => { location.hash = '#/'; },
  });
}

/** Briefing over a running session; the sim waits, and a procedure can be drawn on the scope. */
function openBriefing(session: GameSession | null = game, returnToPause = false): void {
  if (!session || closeOverlay) return;
  session.pause();
  if (session.openLessonBriefing(() => {
    if (returnToPause) openPause();
    else session.resume();
  })) return;
  closeOverlay = showBriefing(overlayRoot, session.airport, {
    closeLabel: 'Back to the scope',
    onClose: () => { hideOverlay(); if (returnToPause) openPause(); else session.resume(); },
    onShowOnScope: (lines) => session.setHighlight(lines),
  });
}

/** Debrief overlay; a timed shift that ran to the end is graded and recorded. */
function showEnd(stats: Stats, reason: 'shift' | 'quit' | 'collision'): void {
  hideOverlay();
  telemetry?.finish();
  const s = sessionSettings ?? settings;
  let shift: { record: ShiftRecord; rank: number | null; best: ShiftRecord[] } | null = null;
  if (s.mode === 'shift' && reason === 'shift' && !game?.sim.practicing) {
    const record: ShiftRecord = { position: s.position, coaching: s.coaching, airport: s.airport, realism: s.realism, traffic: s.traffic, minutes: s.shiftMinutes, score: stats.score, grade: grade(stats), handled: stats.landings + stats.handoffs, at: new Date().toISOString() };
    shift = { record, ...recordScore(storage, record) };
  }
  trackEvent('debrief_view', { mode: s.mode, reason });
  if (s.mode === 'shift' && reason === 'shift') trackEvent('shift_complete', { airport: s.airport, minutes: s.shiftMinutes, grade: grade(stats) });
  closeOverlay = showDebrief(overlayRoot, {
    replay: game ? { airport: game.airport, frames: game.sim.replay() } : undefined,
    onPractice: reason !== 'collision' && s.mode !== 'scenario' ? (timeS) => { if (game?.practiceAt(timeS)) hideOverlay(); } : undefined,
    stats, timeline: game?.sim.timeline(), settings: s, shift, costs: game?.costs(), transcript: game?.transcript(), sessionLog: game?.sessionLog(),
    onAgain: () => { trackEvent('retry', { mode: s.mode }); restart(); },
    onSetup: () => { location.hash = '#/play'; },
    onMenu: () => { location.hash = '#/'; },
    onFeedback: openFeedback,
  });
}

errorEl.addEventListener('click', () => { errorEl.hidden = true; });
if (isDeepLink(startParams) && (location.hash === '' || location.hash === '#/')) history.replaceState(null, '', `${location.pathname}${location.search}#/game`);
window.addEventListener('hashchange', () => void route());
/**
 * Phones and tablets get a one-time note rather than a wall: the menus and lessons work there,
 * but a live shift wants a keyboard and a wide screen.
 */
function deviceNotice(): void {
  const KEY = 'atc.device-notice.v1';
  const small = (() => { try { return matchMedia('(pointer: coarse)').matches || innerWidth < 820; } catch { return false; } })();
  const seen = (() => { try { return storage?.getItem(KEY) === '1'; } catch { return true; } })();
  if (!small || seen) return;
  const el = document.createElement('div');
  el.className = 'device-notice'; el.setAttribute('role', 'status');
  el.innerHTML = '<p><b>Best on a desktop with a keyboard.</b> The menus and Academy work here; a live shift is cramped on a small screen.</p><button type="button">Continue anyway</button>';
  el.querySelector('button')!.addEventListener('click', () => { try { storage?.setItem(KEY, '1'); } catch {} el.remove(); });
  document.body.append(el);
  trackEvent('mobile_notice', { width: innerWidth });
}

async function bootstrap(): Promise<void> {
  // Before the data notice, which holds the first route back: a visit that never gets past it
  // is still a visit.
  trackPageview(location.hash);
  deviceNotice();
  managedConfig = await loadGameConfig();
  settings = resolveSettings(readFromUrl(startParams), readFromStorage(storage), managedConfig);
  if (!managedConfig.allowVoicePlayback) settings.voice = false;
  if (!managedConfig.allowSpeechInput) settings.speechInput = false;
  const acknowledged = (() => { try { return storage?.getItem(NOTICE_KEY) === '1'; } catch { return false; } })();
  if (managedConfig.telemetryEnabled && !acknowledged) {
    closeFeedback = showDataNotice(overlayRoot, () => { try { storage?.setItem(NOTICE_KEY, '1'); } catch {} closeFeedback?.(); closeFeedback = null; void route(); });
  } else void route();
}
void bootstrap();
